/**
 * HTTP abstraction shared by the remote-config poller and the telemetry
 * emitter. Production wires `defaultFetcher` (backed by `globalThis.fetch`);
 * tests pass a fake. Subsystems must NOT call `fetch` directly — that
 * tightly couples them to the runtime and is hostile to testing.
 */

export interface FetcherRequest {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: string;
  signal?: AbortSignal;
}

export interface FetcherResponse {
  status: number;
  /** Header names lowercased — easier to look up portably. */
  headers: Record<string, string>;
  body: string;
}

export interface Fetcher {
  fetch(req: FetcherRequest): Promise<FetcherResponse>;
}

/**
 * Default Fetcher backed by the platform `fetch`. Available in Node 18+,
 * modern browsers, and Android Chrome WebView.
 */
export const defaultFetcher: Fetcher = {
  async fetch(req) {
    const init: RequestInit = {
      method: req.method ?? 'GET',
    };
    if (req.headers) init.headers = req.headers;
    if (req.body !== undefined) init.body = req.body;
    if (req.signal) init.signal = req.signal;

    const res = await globalThis.fetch(req.url, init);
    const headers: Record<string, string> = {};
    res.headers.forEach((value, key) => {
      headers[key.toLowerCase()] = value;
    });
    return {
      status: res.status,
      headers,
      body: await res.text(),
    };
  },
};
